import { useCallback } from 'react';
import {
  signInWithRedirect,
  getCurrentUser,
  signOut as signOutCognito,
  fetchAuthSession,
} from '@aws-amplify/auth';

import { UseAuthType } from '@core/auth/interfaces';

// Cognito managed login supports only these languages (Essentials/Plus plan required).
// Source: https://docs.aws.amazon.com/cognito/latest/developerguide/cognito-user-pools-managed-login.html
const COGNITO_SUPPORTED_LANGS = new Set(['de', 'en', 'es', 'fr', 'it', 'ja', 'ko', 'nl', 'pt', 'zh-cn']);

// App codes that don't match Cognito's expected value directly.
const COGNITO_LOCALE_OVERRIDES: Record<string, string> = {
  pt: 'pt-BR',
  'zh-cn': 'zh-CN',
};

const useAuth: UseAuthType = () => {
  const signIn = useCallback(async (language?: string) => {
    const lang = language?.toLowerCase();
    const cognitoLocale = lang && COGNITO_SUPPORTED_LANGS.has(lang)
      ? (COGNITO_LOCALE_OVERRIDES[lang] ?? lang)
      : undefined;

    // Amplify v6 supports a native `lang` option that appends &lang=<locale>
    // to the /oauth2/authorize request, which Cognito passes through to the
    // hosted UI login page.
    signInWithRedirect(
      cognitoLocale
        ? ({ options: { lang: cognitoLocale } } as any)
        : undefined,
    );
  }, []);

  const signOut = useCallback(async () => {
    signOutCognito({
      global: false,
      oauth: {
        redirectUrl: window.location.origin,
      },
    });
  }, []);

  const authUser = useCallback(async () => {
    return await getCurrentUser();
  }, []);

  const getTokens = useCallback(async (options: any = {}) => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    const session = await fetchAuthSession(options);
    return session?.tokens;
  }, []);

  return { signIn, signOut, getAuthTokens: getTokens, authUser };
};

export default useAuth;
